import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import ReactECharts from "echarts-for-react";
import { Tooltip, message as antMessage } from "antd";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import oneLight from "react-syntax-highlighter/dist/esm/styles/prism/one-light";
import {
  DownloadOutlined,
  CopyOutlined,
  CheckOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { fileIconFor } from "./askai/fileUtils";

// Allow class names on <code>/<pre>, checkbox attrs for task lists, and
// target/rel/title/class on <a> so the agent's pre-rendered source-citation
// anchors survive the sanitize pass.
const sanitizeSchema: any = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), ["className"]],
    pre: [...(defaultSchema.attributes?.pre || []), ["className"]],
    span: [...(defaultSchema.attributes?.span || []), ["className"]],
    a: [
      ...(defaultSchema.attributes?.a || []),
      "target",
      "rel",
      "title",
      "className",
    ],
    input: [
      ...(defaultSchema.attributes?.input || []),
      "type",
      "checked",
      "disabled",
    ],
    li: [...(defaultSchema.attributes?.li || []), "className"],
  },
};

const CHART_LANGS = new Set(["chart", "echarts"]);

// ─────────────────────────────────────────────────────────────────────────────
// CSV utilities
// ─────────────────────────────────────────────────────────────────────────────

function tableToCsv(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll("tr"));
  return rows
    .map((row) => {
      const cells = Array.from(row.querySelectorAll("th, td"));
      return cells
        .map((cell) => {
          const text = (cell.textContent || "").trim();
          // RFC 4180 — wrap in quotes if the value contains comma, quote,
          // or newline; escape inner quotes by doubling.
          if (/[",\n\r]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
          }
          return text;
        })
        .join(",");
    })
    .join("\r\n");
}

function downloadCsv(filename: string, csv: string) {
  // Prefix with BOM so Excel opens UTF-8 cleanly.
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Heuristic: a cell is "numeric-looking" if it matches integers, decimals,
// thousands separators, currency symbols, percentages, signed numbers, or
// common large-number suffixes used in tender/finance contexts
// (K, M, B, L, Cr, Lakh, Crore, etc.). Used to right-align money/score
// columns and apply tabular-num spacing.
const NUMERIC_RE =
  /^\s*-?[$₹€£¥]?\s*[\d,]+(\.\d+)?\s*(%|[KkMmBb]|[Ll]|[Cc]r|[Ll]akh|[Cc]rore)?\s*$/;

// ─────────────────────────────────────────────────────────────────────────────
// TableCard — wraps each markdown table with a toolbar, sticky head, zebra
// rows, numeric column detection, and CSV download.
// ─────────────────────────────────────────────────────────────────────────────

const TableCard: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [rowCount, setRowCount] = useState(0);

  // After render, walk the table DOM: count rows, then right-align and
  // tabular-num any column whose body cells are ≥70% numeric.
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const bodyRows = Array.from(el.querySelectorAll("tbody tr"));
    setRowCount(bodyRows.length);
    if (bodyRows.length === 0) return;
    const colCount = bodyRows[0].children.length;
    const numericCols: boolean[] = [];
    for (let c = 0; c < colCount; c++) {
      let numeric = 0;
      let total = 0;
      bodyRows.forEach((row) => {
        const cell = row.children[c];
        if (!cell) return;
        total += 1;
        const txt = (cell.textContent || "").trim();
        if (txt && NUMERIC_RE.test(txt)) numeric += 1;
      });
      numericCols.push(total > 0 && numeric / total >= 0.7);
    }
    // Apply alignment + mono font to all rows of detected numeric columns.
    const allRows = Array.from(el.querySelectorAll("tr"));
    allRows.forEach((row) => {
      Array.from(row.children).forEach((cell, c) => {
        if (numericCols[c]) {
          const node = cell as HTMLElement;
          node.style.textAlign = "right";
          node.style.fontVariantNumeric = "tabular-nums";
          node.style.fontFeatureSettings = '"tnum"';
        }
      });
    });
  }, [children]);

  const handleDownload = useCallback(() => {
    if (!tableRef.current) return;
    const csv = tableToCsv(tableRef.current);
    downloadCsv(`tender-table-${Date.now()}.csv`, csv);
  }, []);

  return (
    <div
      style={{
        margin: "18px 0",
        border: "1px solid #eef0f3",
        borderRadius: 10,
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      {/* Toolbar — kept low-key so the table itself reads as the content */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 12px",
          background: "#fafbfc",
          borderBottom: "1px solid #eef0f3",
          fontSize: 11.5,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "#475569",
            fontWeight: 500,
          }}
        >
          <TableOutlined style={{ color: "#2563eb", fontSize: 13 }} />
          <span>Table</span>
          {rowCount > 0 && (
            <span style={{ color: "#94a3b8", fontWeight: 400 }}>
              · {rowCount} {rowCount === 1 ? "row" : "rows"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleDownload}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            border: "1px solid #d6e4ff",
            borderRadius: 8,
            background: "#fff",
            color: "#2563eb",
            fontSize: 11.5,
            fontWeight: 500,
            cursor: "pointer",
            transition: "background 0.15s ease, border-color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#eff4fb";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff";
          }}
        >
          <DownloadOutlined style={{ fontSize: 11 }} />
          Download CSV
        </button>
      </div>
      {/* Scroll container — vertical scroll kicks in past ~360px so very tall
          tables don't dominate the message. */}
      <div
        style={{
          overflow: "auto",
          maxHeight: 360,
        }}
      >
        <table
          ref={tableRef}
          className="askai-md-table"
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            fontSize: 13,
          }}
        >
          {children}
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CodeBlock — fenced code with language label and copy button.
// ─────────────────────────────────────────────────────────────────────────────

const CodeBlock: React.FC<{ lang: string; raw: string }> = ({ lang, raw }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(raw);
      } else {
        const ta = document.createElement("textarea");
        ta.value = raw;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard refused — ignore */
    }
  }, [raw]);

  return (
    <div
      className="askai-md-codeblock"
      style={{
        position: "relative",
        margin: "14px 0",
        border: "1px solid #e6e8ef",
        borderRadius: 10,
        overflow: "hidden",
        background: "#fafafa",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 10px",
          background: "#f1f5f9",
          borderBottom: "1px solid #e6e8ef",
          fontSize: 11,
          color: "#475569",
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        <span>{lang}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="askai-md-code-copy"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            border: "none",
            background: "transparent",
            color: copied ? "#16a34a" : "#64748b",
            cursor: "pointer",
            fontSize: 11,
            fontFamily: "inherit",
            padding: "2px 6px",
            borderRadius: 4,
            textTransform: "none",
            letterSpacing: 0,
          }}
          aria-label={copied ? "Code copied" : "Copy code"}
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <SyntaxHighlighter
        language={lang}
        style={oneLight}
        customStyle={{
          margin: 0,
          padding: "10px 12px",
          fontSize: 12.5,
          background: "transparent",
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
          },
        }}
      >
        {raw}
      </SyntaxHighlighter>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline source citation chip — rendered when an <a> link has the text
// pattern "Source: <filename>".
// ─────────────────────────────────────────────────────────────────────────────

const SourceChip: React.FC<{
  href: string;
  filename: string;
  meta?: string;
}> = ({ href, filename, meta }) => {
  // Click/download is temporarily disabled because the file-fetch path
  // is unreliable. Render the chip as a non-interactive badge so users
  // still see the source attribution.
  void href;
  const tipContent = (
    <div style={{ fontSize: 12, lineHeight: 1.45, maxWidth: 280 }}>
      <div
        style={{
          fontWeight: 600,
          wordBreak: "break-all",
        }}
      >
        {filename}
      </div>
      {meta && (
        <div
          style={{
            color: "rgba(255, 255, 255, 0.78)",
            marginTop: 2,
          }}
        >
          {meta}
        </div>
      )}
    </div>
  );
  return (
    <Tooltip
      title={tipContent}
      mouseEnterDelay={0.2}
      overlayStyle={{ maxWidth: 320 }}
    >
      <span
        className="askai-md-source-chip"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "0 7px 0 5px",
          margin: "0 2px",
          background: "#eff4fb",
          border: "1px solid #d6e4ff",
          borderRadius: 999,
          color: "#1e3a8a",
          fontSize: 11,
          fontWeight: 500,
          // Override italic if the chip is inside an <em> — the agent's
          // templates wrap source links in *…* by convention.
          fontStyle: "normal",
          textDecoration: "none",
          lineHeight: 1.8,
          verticalAlign: "baseline",
          maxWidth: "100%",
          cursor: "default",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 11,
            fontStyle: "normal",
          }}
        >
          {fileIconFor(filename)}
        </span>
        <span
          style={{
            maxWidth: meta ? 170 : 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontStyle: "normal",
          }}
        >
          {filename}
        </span>
        {meta && (
          <>
            <span
              aria-hidden
              style={{
                color: "#94a3b8",
                margin: "0 1px",
                fontStyle: "normal",
              }}
            >
              ·
            </span>
            <span
              style={{
                color: "#475569",
                fontWeight: 400,
                fontStyle: "normal",
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {meta}
            </span>
          </>
        )}
      </span>
    </Tooltip>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom task-list checkbox
// ─────────────────────────────────────────────────────────────────────────────

const TaskCheckbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    aria-hidden
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 14,
      height: 14,
      borderRadius: 4,
      border: `1.5px solid ${checked ? "#16a34a" : "#cbd5e1"}`,
      background: checked ? "#16a34a" : "#fff",
      color: "#fff",
      fontSize: 9,
      fontWeight: 700,
      marginRight: 8,
      marginLeft: -22,
      verticalAlign: "middle",
      flexShrink: 0,
    }}
  >
    {checked ? "✓" : ""}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// MarkdownMessage
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  text: string;
  inverted?: boolean;
}

const MarkdownMessage: React.FC<Props> = ({ text, inverted = false }) => {
  const components = useMemo(
    () => ({
      code({ inline, className, children, ...props }: any) {
        const langMatch = /language-([\w-]+)/.exec(className || "");
        const lang = langMatch?.[1]?.toLowerCase();
        const raw = String(children ?? "").replace(/\n$/, "");

        if (!inline && lang && CHART_LANGS.has(lang)) {
          return <ChartBlock raw={raw} />;
        }
        if (inline) {
          return (
            <code
              {...props}
              style={{
                background: inverted ? "rgba(255,255,255,0.18)" : "#f1f5f9",
                color: inverted ? "#fff" : "#1e3a8a",
                padding: "1px 6px",
                borderRadius: 4,
                fontSize: "0.92em",
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
              }}
            >
              {children}
            </code>
          );
        }
        if (lang) {
          return <CodeBlock lang={lang} raw={raw} />;
        }
        return (
          <pre
            style={{
              background: inverted ? "rgba(255,255,255,0.12)" : "#f8fafc",
              color: inverted ? "#fff" : "#0f172a",
              padding: "10px 12px",
              borderRadius: 8,
              border: inverted ? "none" : "1px solid #e6e8ef",
              fontSize: 12.5,
              overflowX: "auto",
              margin: "14px 0",
            }}
          >
            <code className={className}>{children}</code>
          </pre>
        );
      },
      img({ src, alt, title }: any) {
        if (!src) return null;
        return <DownloadableImage src={src} alt={alt} title={title} />;
      },
      a({ href, children, ...props }: any) {
        // Source citations render as inline pills. The link text can be
        // "Source: foo" (from upstream markdown rewrite) or "(Source: foo)"
        // (when the agent already wrapped the anchor in parens).
        const textChildren = React.Children.toArray(children)
          .map((c) => (typeof c === "string" ? c : ""))
          .join("");
        const sourceMatch = textChildren.match(
          /^[(\[\s]*sources?:\s*(.+?)[)\]\s]*$/i
        );

        // Agent also marks citation anchors with `class="src-anchor-inline"`.
        // Some of those carry only an icon/emoji as their text (no
        // "Source: filename" prefix), so the regex above won't match. Pick
        // them up here and fall back to `title` or the URL basename to
        // recover the filename.
        const className: string = (props.className as string) || "";
        const isAnchorClass = /\bsrc-anchor(-inline)?\b/i.test(className);
        const titleAttr: string | undefined = props.title || undefined;
        const isProbablyCitation = href && (sourceMatch || isAnchorClass);

        const basenameFromUrl = (u: string): string => {
          try {
            const path = new URL(u).pathname;
            const last = decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
            return last;
          } catch {
            return "";
          }
        };

        if (isProbablyCitation) {
          // Examples we need to split:
          //   "smoke_1778071537.txt"                          → filename only
          //   "MNR TCN R1.xlsx, sheet \"PARKINGS BOQ\""       → filename + sheet
          //   "report.pdf, page 5"                            → filename + page
          //   "📎 View source: smoke.txt · p.1"               → emoji + prefix + filename · page
          let rawFromText = sourceMatch
            ? sourceMatch[1].trim()
            : (titleAttr || basenameFromUrl(href) || "source").trim();

          // Strip leading paperclip/icon noise + "View source:" / "Source:"
          // prefix the agent often bakes into the title attribute.
          rawFromText = rawFromText
            .replace(/^[^\p{L}\p{N}"'(\[]+/u, "") // leading emoji/symbols
            .replace(/^\s*(view\s+source|source)\s*:\s*/i, "")
            .trim();

          // Either comma or middle dot (·) separates filename from meta.
          const sepMatch = rawFromText.match(/\s*[,·]\s*/);
          const sepIdx = sepMatch ? sepMatch.index! : -1;
          const filename =
            sepIdx === -1 ? rawFromText : rawFromText.slice(0, sepIdx).trim();
          const meta =
            sepIdx === -1
              ? undefined
              : rawFromText
                  .slice(sepIdx + sepMatch![0].length)
                  .trim()
                  // Drop the "sheet "/"page "/"p." prefix so the badge reads
                  // as a value, not "sheet PARKINGS BOQ" or "p.1".
                  .replace(/^(sheet|page)\s+/i, "")
                  .replace(/^p\.\s*/i, "p.")
                  // Strip surrounding quotes the agent often adds.
                  .replace(/^["“']|["”']$/g, "")
                  .replace(/^["“']|["”']$/g, "");
          return (
            <SourceChip href={href} filename={filename} meta={meta} />
          );
        }
        return (
          <a
            {...props}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: inverted ? "#dbeafe" : "#2263b3",
              textDecoration: "underline",
              textUnderlineOffset: 2,
            }}
          >
            {children}
          </a>
        );
      },

      // Table tree — react-markdown passes children straight through;
      // TableCard wraps with toolbar + scroll.
      table({ children }: any) {
        return <TableCard>{children}</TableCard>;
      },
      thead({ children }: any) {
        return (
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {children}
          </thead>
        );
      },
      th({ children }: any) {
        return (
          <th
            style={{
              padding: "10px 14px",
              background: inverted ? "rgba(255,255,255,0.12)" : "#f8fafc",
              color: inverted ? "#fff" : "#475569",
              textAlign: "left",
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              borderBottom: `1px solid ${
                inverted ? "rgba(255,255,255,0.3)" : "#e2e8f0"
              }`,
              whiteSpace: "nowrap",
            }}
          >
            {children}
          </th>
        );
      },
      td({ children }: any) {
        return (
          <td
            style={{
              padding: "10px 14px",
              color: inverted ? "#fff" : "#0f172a",
              borderBottom: `1px solid ${
                inverted ? "rgba(255,255,255,0.18)" : "#f1f5f9"
              }`,
              verticalAlign: "top",
              fontSize: 13.5,
              lineHeight: 1.55,
            }}
          >
            {children}
          </td>
        );
      },

      // Lists — bullets/numbers via ::marker get accented in the global style
      // block; here we just tune spacing.
      ul({ children }: any) {
        return (
          <ul
            style={{
              paddingLeft: 24,
              margin: "4px 0 8px",
              lineHeight: 1.6,
              listStyleType: "disc",
              listStylePosition: "outside",
            }}
          >
            {children}
          </ul>
        );
      },
      ol({ children }: any) {
        return (
          <ol
            style={{
              paddingLeft: 24,
              margin: "4px 0 8px",
              lineHeight: 1.6,
              listStyleType: "decimal",
              listStylePosition: "outside",
            }}
          >
            {children}
          </ol>
        );
      },
      li({ className, children }: any) {
        const isTask =
          typeof className === "string" && className.includes("task-list-item");
        if (isTask) {
          // remark-gfm emits <input type="checkbox" .../> as the first child.
          // We swap it for our custom checkbox and pull the bullet marker
          // off so the row reads cleanly.
          let checked = false;
          const filtered = React.Children.map(children, (child) => {
            if (
              React.isValidElement(child) &&
              (child as any).props?.type === "checkbox"
            ) {
              checked = !!(child as any).props.checked;
              return null;
            }
            return child;
          });
          return (
            <li
              style={{
                marginBottom: 2,
                listStyle: "none",
                paddingLeft: 0,
                color: checked ? "#94a3b8" : undefined,
                textDecoration: checked ? "line-through" : undefined,
              }}
            >
              <TaskCheckbox checked={checked} />
              {filtered}
            </li>
          );
        }
        return <li style={{ marginBottom: 2 }}>{children}</li>;
      },
      // Hide the bare task-list checkbox if it slips through outside an <li>
      input({ type, checked, ...rest }: any) {
        if (type === "checkbox") {
          return <TaskCheckbox checked={!!checked} />;
        }
        return <input type={type} checked={checked} {...rest} />;
      },

      // Heading scale tuned for Claude/ChatGPT-style rhythm. All headings use
      // the same dark slate color (no blue accent) so they look like part of
      // the prose; size + weight drives hierarchy. Top margin is larger than
      // bottom so each heading visually "belongs" to the section below it.
      h1({ children }: any) {
        return (
          <h2
            style={{
              fontSize: 26,
              fontWeight: 700,
              margin: "28px 0 10px",
              color: inverted ? "#fff" : "#0f172a",
              letterSpacing: -0.5,
              lineHeight: 1.22,
            }}
          >
            {children}
          </h2>
        );
      },
      h2({ children }: any) {
        return (
          <h3
            style={{
              fontSize: 21,
              fontWeight: 700,
              margin: "26px 0 8px",
              color: inverted ? "#fff" : "#0f172a",
              letterSpacing: -0.35,
              lineHeight: 1.28,
            }}
          >
            {children}
          </h3>
        );
      },
      h3({ children }: any) {
        return (
          <h4
            style={{
              fontSize: 18,
              fontWeight: 600,
              margin: "22px 0 6px",
              color: inverted ? "#fff" : "#0f172a",
              letterSpacing: -0.2,
              lineHeight: 1.35,
            }}
          >
            {children}
          </h4>
        );
      },
      h4({ children }: any) {
        return (
          <h5
            style={{
              fontSize: 15.5,
              fontWeight: 600,
              margin: "18px 0 4px",
              color: inverted ? "#fff" : "#0f172a",
              letterSpacing: -0.1,
              lineHeight: 1.4,
            }}
          >
            {children}
          </h5>
        );
      },

      p({ children }: any) {
        return (
          <p
            style={{
              margin: "0 0 12px",
              lineHeight: 1.75,
              color: inverted ? undefined : "#0f172a",
            }}
          >
            {children}
          </p>
        );
      },

      strong({ children }: any) {
        // Claude/ChatGPT use semibold (600) for inline emphasis without
        // recoloring — keeps the body color uniform and the flow uninterrupted.
        return <strong style={{ fontWeight: 600 }}>{children}</strong>;
      },

      em({ children }: any) {
        return <em style={{ fontStyle: "italic" }}>{children}</em>;
      },

      del({ children }: any) {
        return (
          <del
            style={{
              color: inverted ? "rgba(255,255,255,0.55)" : "#94a3b8",
              textDecoration: "line-through",
            }}
          >
            {children}
          </del>
        );
      },

      blockquote({ children }: any) {
        return (
          <blockquote
            style={{
              margin: "10px 0",
              padding: "8px 12px",
              borderLeft: `3px solid ${inverted ? "rgba(255,255,255,0.4)" : "#2263b3"}`,
              background: inverted ? "rgba(255,255,255,0.05)" : "#f8fafc",
              color: inverted ? "rgba(255,255,255,0.85)" : "#475569",
              borderRadius: "0 6px 6px 0",
              fontStyle: "italic",
              lineHeight: 1.6,
            }}
          >
            {children}
          </blockquote>
        );
      },

      hr() {
        return (
          <hr
            style={{
              border: "none",
              borderTop: `1px solid ${inverted ? "rgba(255,255,255,0.25)" : "#e6e8ef"}`,
              margin: "14px 0",
            }}
          />
        );
      },
    }),
    [inverted]
  );

  return (
    <>
      <style>{`
        /* Premium typography for the response body — Inter character
           variants give the cleaner geometric look Claude/ChatGPT use. */
        .askai-markdown {
          color: #0f172a;
          font-feature-settings: "cv02", "cv03", "cv04", "cv11", "ss01";
          letter-spacing: -0.005em;
        }
        /* The app's global resets (DashLite + Bootstrap) strip list-style
           from ul/ol with high specificity. Render bullets/numbers via
           ::before pseudo-elements so the marker is independent of the
           global reset — same approach as the AskAI chat surface. */
        .askai-markdown ul, .askai-markdown ol {
          margin: 4px 0 !important;
          padding-left: 22px !important;
          list-style: none !important;
        }
        .askai-markdown li {
          margin-bottom: 2px !important;
          display: list-item !important;
          list-style: none !important;
          position: relative !important;
        }
        .askai-markdown ul > li::before {
          content: "•";
          position: absolute;
          left: -14px;
          top: 0;
          color: #475569;
          font-weight: 700;
          line-height: inherit;
        }
        .askai-markdown ol {
          counter-reset: askai-ol;
        }
        .askai-markdown ol > li {
          counter-increment: askai-ol;
        }
        .askai-markdown ol > li::before {
          content: counter(askai-ol) ".";
          position: absolute;
          left: -22px;
          top: 0;
          width: 18px;
          text-align: right;
          color: #475569;
        }
        /* GFM task list — drop the bullet, the checkbox is the marker. */
        .askai-markdown li.task-list-item::before {
          content: "" !important;
        }
        /* Loose-list mode wraps each <li> in a <p>. Strip the paragraph
           margin so list items don't get the global 10px gap stacked on top
           of their own marginBottom. */
        .askai-markdown li > p {
          margin: 0;
        }
        /* When a list item itself contains a list, keep the nested list
           hugging the parent instead of opening a wide gap. */
        .askai-markdown li > ul,
        .askai-markdown li > ol {
          margin: 2px 0 4px;
        }
        /* First/last block in the markdown shouldn't add margin to the
           container — keeps the bubble snug at the edges. */
        .askai-markdown > :first-child { margin-top: 0; }
        .askai-markdown > :last-child  { margin-bottom: 0; }
        .askai-markdown .askai-md-table tbody tr:nth-child(even) td {
          background: #fafbfc;
        }
        .askai-markdown .askai-md-table tbody tr:hover td {
          background: #f5f8ff;
        }
        .askai-markdown .askai-md-codeblock .askai-md-code-copy {
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .askai-markdown .askai-md-codeblock:hover .askai-md-code-copy,
        .askai-markdown .askai-md-codeblock:focus-within .askai-md-code-copy {
          opacity: 1;
        }
      `}</style>
      <div className="askai-markdown">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          // rehype-raw parses inline HTML (e.g. the agent's pre-rendered
          // <a class="src-anchor-inline">…</a> source citations) into the
          // hast tree; rehype-sanitize then runs over the parsed nodes and
          // strips anything not whitelisted in `sanitizeSchema`.
          rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
          components={components}
        >
          {/* Markdown preprocessing for agent quirks:
              1. Strip stray "-" lines (empty list items the agent emits
                 after citation pills) — see comment further below.
              2. The agent emits section headers like **Pros**, **Cons / risks**,
                 **Verdict** each on its own line with a single \n. CommonMark
                 collapses single newlines into a space, so the sections run
                 together. Promote any single \n that precedes a bold marker
                 ("\n**") into a paragraph break ("\n\n**"). */}
          {text
            .replace(/^[ \t]*-[ \t]*$/gm, "")
            .replace(/([^\n])\n(\*\*)/g, "$1\n\n$2")}
        </ReactMarkdown>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ChartBlock — fenced ```chart payload rendered via ECharts. Unchanged.
// ─────────────────────────────────────────────────────────────────────────────

const ChartBlock: React.FC<{ raw: string }> = ({ raw }) => {
  const { option, error } = useMemo(() => parseChart(raw), [raw]);

  if (error) {
    return (
      <div
        style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 6,
          padding: "8px 10px",
          fontSize: 12,
          color: "#991b1b",
          margin: "10px 0",
          fontFamily: "ui-monospace, monospace",
        }}
      >
        Chart parse error: {error}
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #eef0f3",
        borderRadius: 10,
        padding: 10,
        margin: "14px 0",
      }}
    >
      <ReactECharts
        option={option}
        style={{ height: 260, width: "100%" }}
        notMerge
        lazyUpdate
      />
    </div>
  );
};

function parseChart(raw: string): { option?: any; error?: string } {
  let json: any;
  try {
    json = JSON.parse(raw);
  } catch (e: any) {
    return { error: e?.message || "invalid JSON" };
  }
  if (json && typeof json === "object" && Array.isArray(json.series)) {
    return { option: json };
  }
  const type = String(json?.type || "").toLowerCase();
  const data: any[] = Array.isArray(json?.data) ? json.data : [];
  if (!type || data.length === 0) {
    return { error: "expected { type, data } or a full ECharts option" };
  }
  const title = json.title
    ? {
        title: {
          text: String(json.title),
          left: "center",
          textStyle: { fontSize: 13 },
        },
      }
    : {};
  if (type === "pie" || type === "doughnut") {
    return {
      option: {
        ...title,
        tooltip: { trigger: "item" },
        legend: { bottom: 0, type: "scroll" },
        series: [
          {
            type: "pie",
            radius: type === "doughnut" ? ["40%", "70%"] : "65%",
            data: data.map((d: any) => ({
              name: String(d.label ?? d.name ?? ""),
              value: Number(d.value ?? 0),
            })),
          },
        ],
      },
    };
  }
  const categories = data.map((d: any) => String(d.label ?? d.name ?? ""));
  const values = data.map((d: any) => Number(d.value ?? 0));
  const seriesType =
    type === "area" ? "line" : type === "line" ? "line" : "bar";
  const series: any = { type: seriesType, data: values };
  if (type === "area") series.areaStyle = {};
  return {
    option: {
      ...title,
      tooltip: { trigger: "axis" },
      grid: { left: 40, right: 20, top: title ? 36 : 16, bottom: 32 },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          fontSize: 11,
          interval: 0,
          rotate: categories.length > 6 ? 30 : 0,
        },
      },
      yAxis: { type: "value", axisLabel: { fontSize: 11 } },
      series: [series],
    },
  };
}

// DownloadableImage renders the markdown image inside a rounded card with a
// hover-revealed download button. The agent often replies with a SAS-signed
// Azure blob URL (cross-origin) — the browser ignores the <a download> attr
// for cross-origin sources, so we fetch the bytes, wrap them in an object
// URL, and click a local anchor to force a real download.
interface DownloadableImageProps {
  src: string;
  alt?: string;
  title?: string;
}

const DownloadableImage: React.FC<DownloadableImageProps> = ({ src, alt, title }) => {
  const [downloading, setDownloading] = useState(false);
  const [hover, setHover] = useState(false);

  const filename = useMemo(() => {
    // Derive a sensible name from the URL or the alt text. SAS URLs end in
    // `<name>.png?se=...&sp=...` — strip the query, then take the last segment.
    try {
      const u = new URL(src);
      const last = u.pathname.split("/").filter(Boolean).pop() || "";
      if (last && /\.[a-z0-9]{2,5}$/i.test(last)) return decodeURIComponent(last);
    } catch {
      /* not a URL we can parse — fall through */
    }
    const safeAlt = (alt || "ai-response")
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    return `${safeAlt || "ai-response"}-${Date.now()}.png`;
  }, [src, alt]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(src, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      // CORS or network failure — open the image in a new tab so the user
      // can save it manually via right-click.
      const a = document.createElement("a");
      a.href = src;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      antMessage.info("Opened image in a new tab — right-click to save.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
        margin: "8px 0",
        maxWidth: "100%",
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid #eef0f3",
        background: "#fff",
        lineHeight: 0,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <img
        src={src}
        alt={alt || "Image"}
        title={title || alt}
        style={{
          display: "block",
          maxWidth: "100%",
          height: "auto",
          borderRadius: 10,
        }}
      />
      <Tooltip title="Download">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="Download image"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            background: "rgba(15, 23, 42, 0.7)",
            color: "#fff",
            cursor: downloading ? "wait" : "pointer",
            opacity: hover || downloading ? 1 : 0.55,
            transition: "opacity 0.15s ease, background 0.15s ease",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(6px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "rgba(15, 23, 42, 0.9)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "rgba(15, 23, 42, 0.7)";
          }}
        >
          <DownloadOutlined style={{ fontSize: 15 }} />
        </button>
      </Tooltip>
    </span>
  );
};

export default MarkdownMessage;
